/**
 * Typed API client.
 *
 * Every response is the {ok, data, meta} / {ok, error} envelope defined in
 * docs/API_SPEC.md and produced by core/renderers.py. This module is the only
 * place that knows about the envelope — callers get `data` or a thrown ApiError.
 *
 * Three things it handles that callers should never repeat:
 *   - access-token refresh, with concurrent 401s collapsed into one refresh
 *   - Idempotency-Key on mutating economy calls, so a retry cannot double-credit
 *   - typed error codes, so a caller can branch on LAMP_LEVEL_TOO_LOW rather
 *     than string-matching a message
 */

const BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

/* Mirrors core/errors.py. Keep the two in sync. */
export type ApiErrorCode =
  | 'UNAUTHENTICATED' | 'BANNED' | 'MAINTENANCE'
  | 'NOT_ENOUGH_COINS' | 'NOT_ENOUGH_KYAT'
  | 'AXE_LEVEL_TOO_LOW' | 'LAMP_LEVEL_TOO_LOW' | 'NOT_ENOUGH_ENERGY' | 'TORCH_EMPTY'
  | 'ROCK_NOT_FOUND' | 'ROCK_ALREADY_CLAIMED' | 'NO_ACTIVE_RUN' | 'CANNOT_GO_BACK'
  | 'CAVE_LOCKED'
  | 'AD_NOT_VERIFIED' | 'AD_CAP_REACHED' | 'AD_COOLDOWN'
  | 'WITHDRAW_BELOW_MIN' | 'WITHDRAW_LIMIT' | 'WITHDRAW_NOT_ELIGIBLE' | 'PAYOUT_PROFILE_LOCKED'
  | 'CHARACTER_LOCKED' | 'CHARACTER_MAX_LEVEL'
  | 'NICKNAME_TAKEN' | 'NICKNAME_INVALID' | 'NICKNAME_BLOCKED'
  | 'RATE_LIMITED' | 'CHEAT_SUSPECTED' | 'VALIDATION_ERROR'
  /* SimpleJWT's own spelling. The server now normalises this to
     UNAUTHENTICATED, but an older server — or a future library that invents a
     third name — must not dead-end the player, so the client treats it as a
     synonym rather than trusting the normalisation alone. */
  | 'TOKEN_NOT_VALID'
  | 'PHONE_VERIFICATION_FAILED' | 'PHONE_ALREADY_USED' | 'PHONE_COOLDOWN'
  | 'MUST_BE_AT_SURFACE'
  | 'PROVIDER_TOKEN_INVALID'
  | 'CONFLICT' | 'INTERNAL_ERROR' | 'NETWORK_ERROR';

/**
 * Does this code mean "your credentials are unusable, get new ones"?
 *
 * There is more than one spelling in practice: DRF's NotAuthenticated becomes
 * UNAUTHENTICATED, while SimpleJWT's InvalidToken carries its own
 * `token_not_valid` default code. The server normalises them, but treating both
 * here is what stops a stale token from dead-ending on the error screen with a
 * good refresh token sitting unused — which is exactly what happened.
 */
export function needsReauth(code: string, status?: number): boolean {
  return code === 'UNAUTHENTICATED' || code === 'TOKEN_NOT_VALID' || status === 401;
}

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly details: Record<string, unknown> = {},
    readonly status = 0,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the player can fix this themselves (upgrade, wait, watch an ad). */
  get isActionable(): boolean {
    return [
      'AXE_LEVEL_TOO_LOW', 'LAMP_LEVEL_TOO_LOW', 'NOT_ENOUGH_ENERGY', 'TORCH_EMPTY',
      'NOT_ENOUGH_COINS', 'NOT_ENOUGH_KYAT', 'AD_CAP_REACHED', 'AD_COOLDOWN',
      'WITHDRAW_BELOW_MIN', 'WITHDRAW_NOT_ELIGIBLE',
      /* The player can fix all three: wait, use another number, or re-enter. */
      'PHONE_VERIFICATION_FAILED', 'PHONE_ALREADY_USED', 'PHONE_COOLDOWN',
      /* Actionable: surface and try again. */
      'MUST_BE_AT_SURFACE',
    ].includes(this.code);
  }
}

interface Envelope<T> {
  ok: boolean;
  data?: T;
  meta?: { server_time: string };
  error?: { code: ApiErrorCode; message: string; details?: Record<string, unknown> };
}

/* ── token storage ────────────────────────────────────────────────────────
   Kept behind an interface so the Capacitor shell can swap in secure storage
   without touching call sites. */
export interface TokenStore {
  access: string | null;
  refresh: string | null;
  save(t: { access: string; refresh: string }): void;
  clear(): void;
}

const KEY = 'shwetap.tokens';

export const tokens: TokenStore = {
  access: null,
  refresh: null,
  save(t) {
    this.access = t.access;
    this.refresh = t.refresh;
    try { localStorage.setItem(KEY, JSON.stringify(t)); } catch { /* private mode */ }
  },
  clear() {
    this.access = null;
    this.refresh = null;
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  },
};

(function hydrate() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { access?: string; refresh?: string };
    tokens.access = parsed.access ?? null;
    tokens.refresh = parsed.refresh ?? null;
  } catch { /* ignore */ }
})();

/* ── refresh, collapsed ───────────────────────────────────────────────────
   Several requests can 401 at once. Without this they would each fire their own
   refresh, and token rotation would invalidate the winners. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccess(): Promise<boolean> {
  if (!tokens.refresh) return false;
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: tokens.refresh }),
      });
      const body = (await res.json()) as Envelope<{ access: string; refresh: string }>;
      if (!res.ok || !body.ok || !body.data) { tokens.clear(); return false; }
      tokens.save(body.data);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/* ── the request ──────────────────────────────────────────────────────────── */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Set on economy mutations so a network retry cannot double-credit. */
  idempotent?: boolean;
  signal?: AbortSignal;
  /** Skips the Authorization header. Used by /auth/guest and /gamedata. */
  anonymous?: boolean;
}

const uuid = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

async function send<T>(path: string, opts: RequestOptions, retrying = false): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!opts.anonymous && tokens.access) headers.Authorization = `Bearer ${tokens.access}`;
  if (opts.idempotent) headers['Idempotency-Key'] = uuid();

  let res: Response;
  try {
    /* Built conditionally: exactOptionalPropertyTypes rejects an explicit
       `body: undefined`, and a GET with a body is invalid anyway. */
    const init: RequestInit = { method: opts.method ?? 'GET', headers };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    if (opts.signal) init.signal = opts.signal;
    res = await fetch(`${BASE}${path}`, init);
  } catch (cause) {
    throw new ApiError('NETWORK_ERROR', 'Could not reach the cave. Check your connection.',
      { cause: String(cause) });
  }

  if (res.status === 204) return undefined as T;

  let body: Envelope<T>;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    const parseErr = new ApiError('INTERNAL_ERROR', 'The server sent something unreadable.', {}, res.status);
    console.error(`[API Error ${res.status}] ${opts.method ?? 'GET'} ${path}:`, parseErr.message);
    throw parseErr;
  }

  if (body.ok && body.data !== undefined) return body.data;
  if (body.ok) return undefined as T;

  const code = body.error?.code ?? 'INTERNAL_ERROR';
  const message = body.error?.message ?? 'Something went wrong.';

  /* One transparent retry after a successful refresh. `retrying` stops a loop
     when the refresh itself is what is unauthorised. */
  if (needsReauth(code, res.status) && !retrying && !opts.anonymous) {
    if (await refreshAccess()) return send<T>(path, opts, true);
  }

  const err = new ApiError(code, message, body.error?.details ?? {}, res.status);
  console.error(`[API Error ${res.status}] ${opts.method ?? 'GET'} ${path}: ${code} - ${message}`, body.error?.details ?? {});
  throw err;
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    send<T>(path, { ...opts, method: 'GET' }),

  post: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    send<T>(path, { ...opts, method: 'POST', body }),

  /** Economy mutations. Carries an Idempotency-Key so a retry is safe. */
  mutate: <T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    send<T>(path, { ...opts, method: 'POST', body, idempotent: true }),
};
